import { useSortable } from '@dnd-kit/sortable';
import {
  getHabitEndedDate,
  getHabitEndedLabel,
  primaryGoalId,
  primaryLifeAreaId,
} from '@griply/utils';
import { endOfWeek, startOfWeek } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BusinessChart2,
  Check,
  ChevronLeft,
  IconFormIcons,
  IconFormNames,
  Lock,
} from 'shared/assets/icons';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import { IconButton, Sizes, Variants } from 'shared/components/ui/icon-button';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { Goal } from 'shared/types/goal';
import { Habit } from 'shared/types/habit';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';
import { getScheduleLabel } from 'shared/utils/get-schedule-label';
import { isNumber } from 'shared/utils/is-number';

import { useWeekStatistics } from '../../hooks/use-week-statistics';
import * as Styled from './habit-detail-mobile-card.style';
import { HabitWeek } from './habit-week';

type SortableContainerProps = React.ComponentProps<typeof Styled.Container> & {
  id: ID;
};

const SortableContainer: React.FC<SortableContainerProps> = ({
  id,
  ...rest
}) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    isDragging,
    transform,
    transition,
  } = useSortable({ id });

  return (
    <Styled.Container
      {...rest}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      role="presentation"
      $isDragging={isDragging}
      $transform={transform}
      $transition={transition}
    />
  );
};

export type HabitDetailMobileCardViewProps = {
  habit: Habit;
  goals: Goal[];
  lifeAreas: LifeArea[];
  onClick?: (habit: Habit) => void;
  weekDate: Timestamp;
  weekStartsOn: WeekDays;
  onDate: (id: ID, date: Timestamp) => void;
  onStatistics?: (id: ID) => void;
  isSortable?: boolean;
  // Unique per rendered occurrence; a habit under two goals renders twice.
  sortableId?: ID;
  // The floating copy under the cursor: icon + name only, no week controls.
  isDragPreview?: boolean;
};

export const HabitDetailMobileCardView: React.FC<
  HabitDetailMobileCardViewProps
> = ({
  habit,
  goals,
  lifeAreas,
  onClick,
  weekDate,
  weekStartsOn,
  onDate,
  onStatistics,
  isSortable,
  sortableId,
  isDragPreview,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const activeSchedule = useActiveSchedule(habit.schedules, today);

  const endedLabel = useMemo(
    () => getHabitEndedLabel(getHabitEndedDate(habit.schedules, today), t),
    [habit.schedules, today, t],
  );

  const scheduleText = activeSchedule
    ? getScheduleLabel(activeSchedule, t)
    : endedLabel;
  const linkedGoalId = primaryGoalId(habit);
  const linkedLifeAreaId = primaryLifeAreaId(habit);

  const activeLinkedItem = useMemo(
    () =>
      goals.find(({ id }) => linkedGoalId === id) ??
      lifeAreas.find(({ id }) => id === linkedLifeAreaId),
    [goals, lifeAreas, linkedGoalId, linkedLifeAreaId],
  );
  const linkedItemColor = useMemo(
    () =>
      activeLinkedItem
        ? getLinkedItemColor(activeLinkedItem.id, { goals, lifeAreas })
        : undefined,
    [activeLinkedItem, goals, lifeAreas],
  );

  const { weekStartDate, weekEndDate } = useMemo(
    () => ({
      weekStartDate: startOfWeek(weekDate, {
        weekStartsOn: weekdaysNumberMap[weekStartsOn],
      }),
      weekEndDate: endOfWeek(weekDate, {
        weekStartsOn: weekdaysNumberMap[weekStartsOn],
      }),
    }),
    [weekDate, weekStartsOn],
  );

  const {
    hasSchedule,
    occurrences,
    dateEntries,
    perfectWeek,
    completionPercentage,
  } = useWeekStatistics(habit.schedules, {
    startDate: weekStartDate,
    endDate: weekEndDate,
    weekStartsOn,
    today,
  });

  const showWeekProgress =
    !isDragPreview && !habit.completedAt && !habit.archivedAt && hasSchedule;
  const showProgress = showWeekProgress && isNumber(completionPercentage);

  const _onClick = () =>
    !habit.frozenAt && !habit.archivedAt && onClick?.(habit);

  const _onDate = (date: Timestamp) =>
    !habit.frozenAt && !habit.archivedAt && onDate(habit.id, date);

  const _onStatistics = () => onStatistics?.(habit.id);

  const Container = isSortable ? SortableContainer : Styled.Container;

  return (
    <Container
      id={sortableId ?? habit.id}
      $isDisabled={!!habit.frozenAt || !!habit.archivedAt}
    >
      <Styled.Header onClick={_onClick}>
        <Styled.IconContainer>
          <Icon
            icon={
              IconFormIcons[habit.iconName!] ??
              IconFormIcons[IconFormNames.Repeat]
            }
          />

          {(!!habit.completedAt || !!endedLabel) && (
            <Styled.CompletedContainer>
              <Icon icon={Check} />
            </Styled.CompletedContainer>
          )}
          {!!linkedItemColor && <Styled.ColorDot $color={linkedItemColor} />}
        </Styled.IconContainer>

        <Styled.HeaderContent>
          <Styled.TitleContainer $isFrozen={!!habit.frozenAt}>
            <Styled.Title>{habit.name}</Styled.Title>
            <Styled.ChevronContainer>
              <Icon icon={ChevronLeft} />
            </Styled.ChevronContainer>
          </Styled.TitleContainer>

          <Styled.HeaderMetaContainer>
            {showProgress && (
              <Styled.ProgressContainer>
                <CircleProgress
                  percentage={completionPercentage}
                  isSecondary
                  isSmall
                />
              </Styled.ProgressContainer>
            )}
            {showProgress && (!!scheduleText || !!activeLinkedItem) && (
              <Styled.Dot />
            )}
            {!!scheduleText && (
              <Styled.MetaSpan>{scheduleText}</Styled.MetaSpan>
            )}
            {!!scheduleText && !!activeLinkedItem && <Styled.Dot />}
            {!!activeLinkedItem && (
              <Styled.MetaSpan>{activeLinkedItem.name}</Styled.MetaSpan>
            )}
          </Styled.HeaderMetaContainer>
        </Styled.HeaderContent>

        {!!habit.frozenAt && (
          <Styled.StateIcon>
            <Icon icon={Lock} />
          </Styled.StateIcon>
        )}
      </Styled.Header>

      {showWeekProgress && (
        <Styled.WeekContainer $disabled={!!habit.frozenAt}>
          <HabitWeek
            startDate={weekDate}
            weekStartsOn={weekStartsOn}
            occurrences={occurrences}
            dateEntries={dateEntries}
            isPerfectWeek={!!perfectWeek}
            onDate={_onDate}
          />

          {!!onStatistics && (
            <IconButton
              icon={BusinessChart2}
              onClick={_onStatistics}
              size={Sizes.ExtraLarge}
              variant={Variants.Contained}
            />
          )}
        </Styled.WeekContainer>
      )}
    </Container>
  );
};
